import AbstractTrackingService from '@ve/services/analytics/yandexAnalytics/abstractTrackingService';
import type { IBackboneHotelOffer } from '@ve/types/backboneModels/Domain/ObjectValue';
import { ProductType } from '@ve/types/ProductType';
import { OFFER_CLICK_SEND_TIMEOUT_MS } from '../../config';
import { OffersFunnel, type ProviderOfferData } from '../../model/types';
import {
  OFFER_ACTION,
  getOfferGoal,
  buildRoomProvidersAnalyticsContext,
  buildSuppliersMap,
  buildSupplierTypesMap,
  getMinimalPrice,
  getSupplierName,
  getSupplierPosition,
  getSupplierType,
  getTravelataPrice,
  type RoomProvidersAnalyticsContext,
  type SearchIds,
} from './analyticsHelpers';

export default class RoomProvidersTrackingService extends AbstractTrackingService {
  private static instance: RoomProvidersTrackingService;

  static getInstance(): RoomProvidersTrackingService {
    if (!RoomProvidersTrackingService.instance) {
      RoomProvidersTrackingService.instance = new RoomProvidersTrackingService();
    }
    return RoomProvidersTrackingService.instance;
  }

  roomBlockOpen(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.setTrackingGoal(getOfferGoal(funnel, OFFER_ACTION.ROOM_BLOCK_OPEN), 'click', {
      ...context,
      price_from: getMinimalPrice(providers),
      price_travelata: getTravelataPrice(providers),
    });
  }

  roomBlockClose(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.setTrackingGoal(getOfferGoal(funnel, OFFER_ACTION.ROOM_BLOCK_CLOSE), 'click', {
      ...context,
      price_from: getMinimalPrice(providers),
      price_travelata: getTravelataPrice(providers),
      suppliers: buildSuppliersMap(providers),
    });
  }

  trackBlockImpression(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.setTrackingGoal(getOfferGoal(funnel, OFFER_ACTION.BLOCK_IMPRESSION), 'other', {
      ...context,
      price_from: getMinimalPrice(providers),
      price_travelata: getTravelataPrice(providers),
      suppliers: buildSuppliersMap(providers),
      supplier_types: buildSupplierTypesMap(providers),
      suppliers_count: providers.length,
    });
  }

  moreSuppliers(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.setTrackingGoal(getOfferGoal(funnel, OFFER_ACTION.MORE_SUPPLIERS), 'click', {
      ...context,
      price_from: getMinimalPrice(providers),
      price_travelata: getTravelataPrice(providers),
      suppliers: buildSuppliersMap(providers),
      supplier_types: buildSupplierTypesMap(providers),
      suppliers_count: providers.length,
    });
  }

  offerClick(
    funnel: OffersFunnel,
    context: RoomProvidersAnalyticsContext,
    providers: ProviderOfferData[],
    provider: ProviderOfferData,
    onSent?: () => void,
  ): void {
    this.setTrackingGoal(
      getOfferGoal(funnel, OFFER_ACTION.OFFER_CLICK),
      'click',
      {
        ...context,
        price_from: getMinimalPrice(providers),
        price_travelata: getTravelataPrice(providers),
        supplier: getSupplierName(provider),
        price_supplier: provider.price,
        supplier_type: getSupplierType(provider.operatorId),
        position: getSupplierPosition(providers, provider),
      },
      onSent,
    );
  }

  private trackSupplierGoal(
    goal: string,
    context: RoomProvidersAnalyticsContext,
    providers: ProviderOfferData[],
    provider: ProviderOfferData,
  ): void {
    this.setTrackingGoal(goal, 'click', {
      ...context,
      price_from: getMinimalPrice(providers),
      supplier: getSupplierName(provider),
      supplier_type: getSupplierType(provider.operatorId),
    });
  }

  modalConfirmClick(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[], provider: ProviderOfferData): void {
    this.trackSupplierGoal(getOfferGoal(funnel, OFFER_ACTION.MODAL_CONFIRM_CLICK), context, providers, provider);
  }

  dismissPartnerModal(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[], provider: ProviderOfferData): void {
    this.trackSupplierGoal(getOfferGoal(funnel, OFFER_ACTION.MODAL_STAY_CLICK), context, providers, provider);
  }

  private trackTariffsGoal(goal: string, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.setTrackingGoal(goal, 'click', {
      ...context,
      price_from: getMinimalPrice(providers),
      price_travelata: getTravelataPrice(providers),
      supplier: 'travelata',
    });
  }

  tariffsOpen(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.trackTariffsGoal(getOfferGoal(funnel, OFFER_ACTION.TARIFFS_OPEN), context, providers);
  }

  tariffsClick(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.trackTariffsGoal(getOfferGoal(funnel, OFFER_ACTION.TARIFFS_CLICK), context, providers);
  }

  tariffsClose(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.trackTariffsGoal(getOfferGoal(funnel, OFFER_ACTION.TARIFFS_CLOSE), context, providers);
  }

  private trackTooltipGoal(goal: string, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.setTrackingGoal(goal, 'click', {
      ...context,
      price_from: getMinimalPrice(providers),
      price_travelata: getTravelataPrice(providers),
      suppliers: buildSuppliersMap(providers),
    });
  }

  trustTooltipOpen(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.trackTooltipGoal(getOfferGoal(funnel, OFFER_ACTION.TRUST_TOOLTIP_OPEN), context, providers);
  }

  trustTooltipClose(funnel: OffersFunnel, context: RoomProvidersAnalyticsContext, providers: ProviderOfferData[]): void {
    this.trackTooltipGoal(getOfferGoal(funnel, OFFER_ACTION.TRUST_TOOLTIP_CLOSE), context, providers);
  }
}

interface CreateRoomProvidersTrackingParams {
  productType: ProductType;
  getProduct: () => IBackboneHotelOffer | null;
  getProviders: () => ProviderOfferData[];
  getSearchIds: () => SearchIds;
}

interface RoomProvidersTracking {
  offerClick: (offer: ProviderOfferData) => Promise<void>;
  modalConfirm: (offer: ProviderOfferData) => void;
  dismissPartnerModal: (offer: ProviderOfferData) => void;
  roomBlockOpen: () => void;
  roomBlockClose: () => void;
  tariffsOpen: () => void;
  tariffsClick: () => void;
  tariffsClose: () => void;
  trustTooltipOpen: () => void;
  trustTooltipClose: () => void;
  moreSuppliers: () => void;
  observeImpression: (target: Element | null | undefined, alreadySent: boolean, onImpression: () => void) => void;
  disconnect: () => void;
}

export const createRoomProvidersTracking = ({
  productType,
  getProduct,
  getProviders,
  getSearchIds,
}: CreateRoomProvidersTrackingParams): RoomProvidersTracking => {
  const funnel = productType === ProductType.HotelOfferProduct ? OffersFunnel.Hotel : OffersFunnel.Tour;
  const service = RoomProvidersTrackingService.getInstance();
  const getContext = () => buildRoomProvidersAnalyticsContext(getProduct(), getSearchIds());

  let impressionObserver: IntersectionObserver | null = null;

  const offerClick = (offer: ProviderOfferData): Promise<void> =>
    new Promise((resolve) => {
      const timeoutId = window.setTimeout(resolve, OFFER_CLICK_SEND_TIMEOUT_MS);
      service.offerClick(funnel, getContext(), getProviders(), offer, () => {
        window.clearTimeout(timeoutId);
        resolve();
      });
    });

  const modalConfirm = (offer: ProviderOfferData): void => {
    service.modalConfirmClick(funnel, getContext(), getProviders(), offer);
  };

  const dismissPartnerModal = (offer: ProviderOfferData): void => {
    service.dismissPartnerModal(funnel, getContext(), getProviders(), offer);
  };

  const roomBlockOpen = (): void => {
    service.roomBlockOpen(funnel, getContext(), getProviders());
  };

  const roomBlockClose = (): void => {
    service.roomBlockClose(funnel, getContext(), getProviders());
  };

  const tariffsOpen = (): void => {
    service.tariffsOpen(funnel, getContext(), getProviders());
  };

  const tariffsClick = (): void => {
    service.tariffsClick(funnel, getContext(), getProviders());
  };

  const tariffsClose = (): void => {
    service.tariffsClose(funnel, getContext(), getProviders());
  };

  const trustTooltipOpen = (): void => {
    service.trustTooltipOpen(funnel, getContext(), getProviders());
  };

  const trustTooltipClose = (): void => {
    service.trustTooltipClose(funnel, getContext(), getProviders());
  };

  const moreSuppliers = (): void => {
    service.moreSuppliers(funnel, getContext(), getProviders());
  };

  const observeImpression = (
    target: Element | null | undefined,
    alreadySent: boolean,
    onImpression: () => void,
  ): void => {
    if (!target || impressionObserver || alreadySent) return;
    const providers = getProviders();
    if (providers.length === 0) return;

    let fired = false;
    impressionObserver = new IntersectionObserver((entries) => {
      if (fired || !entries[0]?.isIntersecting) return;
      fired = true;
      onImpression();
      service.trackBlockImpression(funnel, getContext(), providers);
      impressionObserver?.disconnect();
    });
    impressionObserver.observe(target);
  };

  const disconnect = (): void => {
    impressionObserver?.disconnect();
    impressionObserver = null;
  };

  return {
    offerClick,
    modalConfirm,
    dismissPartnerModal,
    roomBlockOpen,
    roomBlockClose,
    tariffsOpen,
    tariffsClick,
    tariffsClose,
    trustTooltipOpen,
    trustTooltipClose,
    moreSuppliers,
    observeImpression,
    disconnect,
  };
};
